/**
 * Les lectures du bilan de séance : la clôture qualité (`getQualityClosure` — « Les bases », les
 * alertes de chaque élève) et l'occurrence (`getLiveOccurrence` — les rapports de live, la note de
 * la plateforme), au montage, comme l'ancien écran.
 *
 * Le score oral arrive après la clôture (40 à 120 s) : tant qu'il manque sur un élève venu,
 * l'occurrence se relit toutes les 5 s, 48 fois au plus (4 min : deux séances mesurées à 38/55 s puis
 * 113/117 s), et seulement quand l'écran est montré. Une relecture qui n'apporte aucun score ne
 * change rien à l'écran ; quand un score atterrit, la clôture se relit une fois : « Productivité
 * basse » vient d'elle, et son cache tombe avec le score.
 *
 * Patron B : les réponses restent ici. La dernière reçue reste à l'écran pendant une relecture :
 * seul le premier chargement montre le squelette.
 */
import { useIsFocused } from "@react-navigation/native"
import { useCallback, useEffect, useRef, useState } from "react"

import { liveService } from "../../../../../../data/services/liveService"
import {
    awaitsOralScore,
    type ApiCloseOutOccurrence,
    type ApiQualityClosure,
} from "../../../../adapters/tutor/Lives/tutorCloseOutAdapter"
import { useApiFetch } from "../../../hooks/useApiFetch"

const ORAL_SCORE_POLL_MS = 5000
const ORAL_SCORE_POLL_MAX = 48

/** Ce que les relectures attendent : le score oral de chaque rapport. */
const oralScoresOf = (occurrence: ApiCloseOutOccurrence | null) =>
    JSON.stringify((occurrence?.live_reports ?? []).map((report) => report.statistics?.oral_productivity ?? null))

export function useTutorCloseOutData(occurrenceId: string | number | null) {
    const enabled = occurrenceId != null
    const routeParams = { id: occurrenceId }
    const requestKey = enabled ? String(occurrenceId) : null
    const focused = useIsFocused()
    const [closure, setClosure] = useState<ApiQualityClosure | null>(null)
    const [occurrence, setOccurrence] = useState<ApiCloseOutOccurrence | null>(null)
    /** Chaque réponse de l'occurrence, même sans rien de neuf : la relecture suivante se cale sur elle. */
    const [answers, setAnswers] = useState(0)

    const closureRead = useApiFetch<ApiQualityClosure>({
        service: liveService.getQualityClosure,
        routeParams,
        requestKey,
        enabled,
        onSuccess: (data) => setClosure(data ?? {}),
    })
    const fetchClosure = closureRead.fetch
    const occurrenceRead = useApiFetch<ApiCloseOutOccurrence>({
        service: liveService.getLiveOccurrence,
        routeParams,
        requestKey,
        enabled,
        onSuccess: (data) => {
            const next = data ?? {}
            setAnswers((count) => count + 1)
            if (occurrence != null && oralScoresOf(next) === oralScoresOf(occurrence)) return
            if (occurrence != null) fetchClosure()
            setOccurrence(next)
        },
    })
    const fetchOccurrence = occurrenceRead.fetch

    const refetch = useCallback(() => {
        fetchClosure()
        fetchOccurrence()
    }, [fetchClosure, fetchOccurrence])

    const pollsRef = useRef(0)
    useEffect(() => {
        if (!focused || !awaitsOralScore(occurrence) || pollsRef.current >= ORAL_SCORE_POLL_MAX) return
        const timer = setTimeout(() => {
            pollsRef.current += 1
            fetchOccurrence()
        }, ORAL_SCORE_POLL_MS)
        return () => clearTimeout(timer)
    }, [occurrence, answers, focused, fetchOccurrence])

    const shown = closure != null && occurrence != null
    const error = shown ? null : (closureRead.error ?? occurrenceRead.error ?? null)
    return {
        closure,
        occurrence,
        loading: enabled && !shown && error == null,
        error: enabled ? error : "Ce bilan est introuvable.",
        refetch,
    }
}
